// SLEEPSAKE (DEV-314): drives the announcement bar in calendar mode and the
// stock-days line on the product page. Both start from Liquid's render and
// then re-decide from the browser clock, because a cached page can be from
// the other side of a midnight flip; at the sale's end the bar swaps to the
// static line in place (D9). The maths lives in @theme/sale-phase.
//
// Every edge is the visitor's own midnight (DEV-706): which sale is on, label
// or timer days, a real timer's 23:59:59, the midnight clock and the stock
// count all follow the browser's zone. Liquid renders on store time, so a
// visitor whose date differs from Melbourne's sees its line until this
// module's first tick replaces it.
//
// DEV-706: also drives the bar in midnight mode (a non-calendar country the
// section's "Count down to local midnight in" setting names): the clock
// counts to the visitor's own next midnight and rolls over to 23:59:59 in
// place, and the message is re-picked from the dated blocks Liquid ships on
// the visitor's own date, so a sale's name changes at their midnight too.
// Liquid's pick (on the blocks' LA/London instants) is the first paint.
//
// Test overrides (sk_clock, sk_end_in, sk_country, sk_preorder) are read only
// when the element carries data-test-hooks="1", which Liquid sets on
// unpublished themes; the live theme never honours them. In midnight mode
// only sk_clock means anything: the blocks shipped are the real country's.

import {
  clockParts,
  localDate,
  localMessage,
  midnightClockSeconds,
  phaseAt,
  readOverrides,
  STORE_TZ,
  visitorZone,
} from "@theme/sale-phase";

const LEGACY_KEY_PREFIX = "announcement-bar-timer-";
const DAY_MS = 86_400_000;

function readCalendar() {
  const node = document.getElementById("sk-sale-calendar");
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (_) {
    return null;
  }
}

// The fake clock from the evergreen timer, per visitor. Removing it is what
// keeps a returning visitor from ever seeing that countdown restart. (The
// session's dismissal flag is in sessionStorage, untouched by this.)
function dropLegacyState() {
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LEGACY_KEY_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch (_) {
    // storage unavailable
  }
}

/** A clock that ticks forward from an override instant when one is given. */
function makeClock(overrides) {
  const startedAt = Date.now();
  const base = overrides.clock ?? startedAt;
  return () => base + (Date.now() - startedAt);
}

function withOverrides(state, overrides, now) {
  if (overrides.endIn !== undefined && state.sale) {
    const endsAt = state.startedAt + overrides.endIn;
    if (now <= endsAt) {
      return { ...state, phase: "timer", endsAt, stockVisible: false, line: state.sale.line };
    }
    return { ...state, phase: "none", sale: null, endsAt: null, line: state.staticLine, stockVisible: true };
  }
  return state;
}

/** The bar's HRS : MIN : SEC tiles (and DAYS, in calendar mode). */
class ClockTiles {
  constructor(el) {
    this.units = {
      days: el.querySelector("[data-days]"),
      hours: el.querySelector("[data-hours]"),
      minutes: el.querySelector("[data-minutes]"),
      seconds: el.querySelector("[data-seconds]"),
    };
    this.daysUnit = el.querySelector("[data-days-unit]");
    this.daysSeparator = el.querySelector("[data-days-separator]");
    this.countdown = el.querySelector(".announcement-bar-timer__countdown");
  }

  render(remainingMs, withDays) {
    const parts = clockParts(remainingMs, withDays);
    this.countdown?.removeAttribute("data-clock-pending");
    if (this.daysUnit) this.daysUnit.hidden = !withDays;
    if (this.daysSeparator) this.daysSeparator.hidden = !withDays;
    this.setUnit("days", parts.days);
    this.setUnit("hours", parts.hours);
    this.setUnit("minutes", parts.minutes);
    this.setUnit("seconds", parts.seconds);
  }

  setUnit(name, value) {
    const node = this.units[name];
    if (!node) return;
    const formatted = String(value).padStart(2, "0");
    if (node.textContent !== formatted) node.textContent = formatted;
  }
}

/** Ticks once a second and again when the tab comes back. */
class Ticker {
  constructor(tick) {
    this.tick = tick;
    this.onVisibility = () => {
      if (!document.hidden) this.tick();
    };
    this.tick();
    this.interval = window.setInterval(this.tick, 1000);
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  destroy() {
    window.clearInterval(this.interval);
    document.removeEventListener("visibilitychange", this.onVisibility);
  }
}

function announcePhase(phase) {
  const root = document.documentElement;
  if (root.dataset.salePhase === phase) return;
  root.dataset.salePhase = phase;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "sale_phase", sale_phase: phase });
}

class SaleTimerBar {
  constructor(el, calendar) {
    this.el = el;
    this.calendar = calendar;
    this.overrides = readOverrides(window.location.search, el.dataset.testHooks === "1");
    this.country = this.overrides.country || el.dataset.country;
    this.now = makeClock(this.overrides);
    this.startedAt = this.now();
    this.zone = visitorZone(calendar.tz || STORE_TZ);
    this.message = el.querySelector("[data-message]");
    this.countdown = el.querySelector(".announcement-bar-timer__countdown");
    this.tiles = new ClockTiles(el);
    this.state = null;
    this.ticker = new Ticker(this.tick.bind(this));
  }

  destroy() {
    this.ticker.destroy();
  }

  compute() {
    const now = this.now();
    const state = phaseAt(this.calendar, this.country, now, this.zone);
    state.startedAt = this.startedAt;
    return withOverrides(state, this.overrides, now);
  }

  tick() {
    const state = this.compute();
    if (!this.state || state.phase !== this.state.phase || state.line !== this.state.line) {
      this.render(state);
    }
    this.state = state;
    if (state.phase === "timer") {
      const remainingMs = state.endsAt - this.now();
      this.tiles.render(remainingMs, remainingMs >= DAY_MS);
    } else if (state.phase === "midnight") {
      this.tiles.render(midnightClockSeconds(this.now(), this.zone) * 1000, false);
    }
  }

  render(state) {
    // The calendar's line is shown as written; a sale that wants "ENDS IN"
    // carries it in its own line (Ali, 17 Sep: the gift-card sales do not).
    if (this.message) this.message.textContent = state.line;
    if (this.countdown) this.countdown.hidden = state.phase !== "timer" && state.phase !== "midnight";
    this.el.dataset.phase = state.phase;
    announcePhase(state.phase);
    // The stock line and the sticky-height code follow the bar's phase
    // change at once rather than on their own slower clocks.
    document.dispatchEvent(new CustomEvent("sk:sale-phase", { detail: { phase: state.phase } }));
  }
}

function readLocalMessages(el) {
  const node = el.querySelector("script[data-local-messages]");
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (_) {
    return null;
  }
}

/**
 * Midnight mode: the dated message and a clock, both on the visitor's date.
 * Exported for theme-tests/sale-timer-midnight-bar.test.mjs only.
 */
export class MidnightBar {
  constructor(el) {
    const overrides = readOverrides(window.location.search, el.dataset.testHooks === "1");
    this.el = el;
    this.now = makeClock(overrides);
    // Without a usable zone both fall back to the device clock.
    this.zone = visitorZone(undefined);
    this.messages = readLocalMessages(el);
    this.message = el.querySelector("[data-message]");
    this.today = null;
    this.tiles = new ClockTiles(el);
    this.ticker = new Ticker(() => this.tick());
  }

  tick() {
    const now = this.now();
    const today = localDate(now, this.zone);
    if (today !== this.today) {
      this.today = today;
      this.applyMessage(today);
    }
    this.tiles.render(midnightClockSeconds(now, this.zone) * 1000, false);
  }

  applyMessage(today) {
    if (!this.messages) return;
    const { message, hide } = localMessage(this.messages, today);
    let changed = false;
    if (this.message && this.message.textContent !== message) {
      this.message.textContent = message;
      changed = true;
    }
    if (this.el.hidden !== hide) {
      this.el.hidden = hide;
      changed = true;
    }
    // Refit the message and redo the sticky height, as at a phase change.
    if (changed) document.dispatchEvent(new CustomEvent("sk:sale-phase", { detail: { phase: "midnight" } }));
  }

  destroy() {
    this.ticker.destroy();
  }
}

export class StockDaysLine {
  constructor(el, calendar) {
    this.el = el;
    this.calendar = calendar;
    this.overrides = readOverrides(window.location.search, el.dataset.testHooks === "1");
    this.country = this.overrides.country || el.dataset.country;
    this.now = makeClock(this.overrides);
    this.startedAt = this.now();
    this.zone = visitorZone(calendar.tz || STORE_TZ);
    this.count = el.querySelector("[data-stock-days]");
    this.preorder = el.dataset.preorder === "1" || this.overrides.preorder === true;
    this.duringTimer = el.dataset.duringTimer === "1";
    // "Show in every country": the no-number look needs no calendar entry.
    this.everyCountry = el.dataset.everyCountry === "1";
    // The theme editor keeps the block visible so it can be selected.
    this.pinned = el.dataset.designMode === "1";
    this.tick = this.tick.bind(this);
    this.tick();
    // The count only changes at local midnight; a minute is plenty, and the
    // bar's phase-change event covers the seam itself.
    this.interval = window.setInterval(this.tick, 60_000);
    document.addEventListener("sk:sale-phase", this.tick);
  }

  destroy() {
    window.clearInterval(this.interval);
    document.removeEventListener("sk:sale-phase", this.tick);
  }

  tick() {
    const now = this.now();
    let state = phaseAt(this.calendar, this.country, now, this.zone);
    state.startedAt = this.startedAt;
    state = withOverrides(state, this.overrides, now);
    const onCalendar = (state.stockVisible || this.duringTimer) && Boolean(state.staticLine);
    const visible = this.pinned || ((this.everyCountry || onCalendar) && !this.preorder);
    this.el.hidden = !visible;
    const days = String(state.stockDays);
    // Only touch the text when it changes: the element is a live region.
    if (this.count && this.count.textContent !== days) this.count.textContent = days;
    this.el.dataset.stockDays = days;
  }
}

const controllers = new WeakMap();

function wire(root, calendar) {
  root.querySelectorAll('announcement-bar-timer[data-timer-mode="midnight"]').forEach((el) => {
    if (!controllers.has(el)) controllers.set(el, new MidnightBar(el));
  });
  if (!calendar) return;
  root.querySelectorAll('announcement-bar-timer[data-timer-mode="calendar"]').forEach((el) => {
    if (!controllers.has(el)) controllers.set(el, new SaleTimerBar(el, calendar));
  });
  root.querySelectorAll("stock-days-line").forEach((el) => {
    if (!controllers.has(el)) controllers.set(el, new StockDaysLine(el, calendar));
  });
}

function unwire(root) {
  root.querySelectorAll("announcement-bar-timer, stock-days-line").forEach((el) => {
    const controller = controllers.get(el);
    if (controller) {
      controller.destroy();
      controllers.delete(el);
    }
  });
}

// A malformed calendar row must not take the whole correction layer down
// with it: the Liquid render stays, and the console says why.
function safely(fn) {
  try {
    fn();
  } catch (error) {
    console.warn("sale-timer: correction disabled", error);
  }
}

function boot() {
  dropLegacyState();
  safely(() => wire(document, readCalendar()));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}

// The theme editor swaps a section's DOM in place; a module runs once, so
// the new elements are wired here and the old controllers' timers stopped.
if (typeof Shopify !== "undefined" && Shopify.designMode) {
  document.addEventListener("shopify:section:unload", (event) => unwire(event.target));
  document.addEventListener("shopify:section:load", (event) => {
    safely(() => wire(event.target, readCalendar()));
  });
}
